/**
 * PII / identifier detectors.
 *
 * Two shapes per concept:
 *  - a GLOBAL regex (used to redact matches *inside* free-text strings), and
 *  - an ANCHORED regex (used to test whether a whole URL path segment is an id).
 *
 * These are intentionally broad. Over-redaction (e.g. a non-PII 24-char token that
 * looks like a cuid) is an accepted trade-off: this is a privacy control, so a
 * false positive costs debuggability, a false negative leaks PII.
 */

// Ballerine cuid v1 (Prisma `@default(cuid())`): 'c' + 24 base36 chars.
const CUID_V1 = 'c[a-z0-9]{24}';
// cuid2 (`@paralleldrive/cuid2`, default length 24): letter + 23 alphanumerics.
const CUID_V2 = '[a-z][a-z0-9]{23}';
const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
// EU-style VAT number: known country prefix + 8–12 alphanumerics.
const VAT_COUNTRY =
  'AT|BE|BG|CY|CZ|DE|DK|EE|EL|ES|FI|FR|GB|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK|XI';

/** Detectors applied to free-text string VALUES; each match is replaced with the filtered token. */
export const VALUE_DETECTORS: readonly RegExp[] = [
  // email
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  // JWT (header.payload.signature)
  /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g,
  // IBAN: country code + 2 check digits + 11–30 alphanumerics
  /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/g,
  // VAT identifier
  new RegExp(`\\b(?:${VAT_COUNTRY})[A-Z0-9]{8,12}\\b`, 'g'),
  // UUID
  new RegExp(UUID, 'g'),
  // long hex token / session id / API key (md5, sha, random hex ≥ 32)
  /\b[0-9a-f]{32,}\b/g,
  // cuid v1 and cuid2
  new RegExp(`\\b${CUID_V1}\\b`, 'g'),
  new RegExp(`\\b${CUID_V2}\\b`, 'g'),
];

/** Anchored detectors used to decide whether a single URL path segment is an identifier. */
export const SEGMENT_DETECTORS: readonly RegExp[] = [
  /^\d+$/, // numeric id
  new RegExp(`^${UUID}$`, 'i'),
  new RegExp(`^${CUID_V1}$`),
  new RegExp(`^${CUID_V2}$`),
  /^[0-9a-f]{16,}$/i, // long hex id
];
